import { Avatar, Button, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemText, Tooltip } from "@mui/material";
import { CircularLoading } from "Common/Components";
import { BugReportOutlined, PolicyOutlined, DescriptionOutlined, TipsAndUpdatesOutlined } from "@mui/icons-material";
import { ProfileUserOutlined, ProviderOutlined } from "Icons/Icons";
import { useHeaderPopup } from "../../Hooks";
import { useGetMyDetailsQuery } from "Services/Admin";
import React from "react";
import { nameToInitials, stringAvatar } from "Common/Functions";
import { HeaderListItem, HeaderPopup } from "../../Components";
import { useLogout } from "Common/Hooks";

interface IProfile {
  handleProfileDrawerOpen: () => void;
  handleReportBugDrawerOpen: () => void;
  handleTermsOfServiceDialogOpen: () => void;
  handleTipsDialogOpen: () => void;
  handlePrivacyPolicyDialogOpen: () => void;
}
export const Profile: React.FC<IProfile> = (props) => {
  const {
    handleProfileDrawerOpen,
    handleReportBugDrawerOpen,
    handleTermsOfServiceDialogOpen,
    handleTipsDialogOpen,
    handlePrivacyPolicyDialogOpen,
  } = props;
  const { anchorRef, open, handleToggle, handleClose } = useHeaderPopup();
  const { handleLogout } = useLogout();
  const { data: userDetails, isLoading: isUserDetailsLoading } = useGetMyDetailsQuery();
  return (
    <React.Fragment>
      <IconButton
        onClick={handleToggle}
        ref={anchorRef}
        size="small"
        sx={(theme) => ({
          padding: 0,
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary.main,
          fontSize: theme.typography.body1.fontSize,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
          },
        })}
        aria-label="profile"
        aria-haspopup="true"
      >
        <Avatar
          variant="circular"
          alt="profile user"
          sx={(theme) => ({
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontSize: theme.typography.body1.fontSize,
            width: theme.spacing(4.5),
            height: theme.spacing(4.5),
          })}
        >
          {nameToInitials(userDetails?.displayName ?? "")}
        </Avatar>
      </IconButton>

      <HeaderPopup anchorEl={anchorRef.current} open={open} handleClose={handleClose} minWidth={320}>
        <CircularLoading isLoading={isUserDetailsLoading}>
          <List
            component="nav"
            sx={(theme) => ({
              flex: 1,
              px: theme.spacing(2),
              py: theme.spacing(1),
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            })}
          >
            <ListItem disableGutters>
              <ListItemAvatar>
                <Avatar
                  alt="profile user"
                  sx={(theme) => ({
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    fontSize: theme.typography.body1.fontSize,
                  })}
                  {...stringAvatar(userDetails?.displayName ?? "")}
                />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Tooltip title={userDetails?.displayName ?? ""} arrow>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "250px",
                      }}
                    >
                      {userDetails?.displayName}
                    </span>
                  </Tooltip>
                }
                secondary={
                  <Tooltip title={userDetails?.email ?? ""} arrow>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "250px",
                      }}
                    >
                      {userDetails?.email ?? ""}
                    </span>
                  </Tooltip>
                }
                slotProps={{ secondary: { variant: "body1" } }}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Avatar
                  alt="profile user"
                  variant="rounded"
                  sx={(theme) => ({
                    bgcolor: theme.palette.grey[200],
                    color: theme.palette.text.primary,
                    borderRadius: theme.spacing(1),
                    fontSize: theme.typography.body1.fontSize,
                  })}
                >
                  <ProviderOutlined />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Tooltip title={userDetails?.entity?.displayName ?? ""} arrow>
                    <span
                      style={{
                        maxWidth: "250px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {userDetails?.entity?.displayName ?? ""}
                    </span>
                  </Tooltip>
                }
                secondary={
                  <Tooltip title={userDetails?.role?.displayName ?? ""} arrow>
                    <span
                      style={{
                        maxWidth: "250px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {userDetails?.role?.displayName ?? ""}
                    </span>
                  </Tooltip>
                }
                slotProps={{
                  secondary: {
                    variant: "body1",
                  },
                }}
              />
            </ListItem>
            <Divider variant="fullWidth" component="li" />
            <HeaderListItem
              primaryText="My Profile"
              icon={<ProfileUserOutlined />}
              onClick={() => {
                handleProfileDrawerOpen();
                handleToggle();
              }}
            />
            <Divider variant="fullWidth" component="li" />
            <HeaderListItem
              primaryText="Report a Bug"
              icon={<BugReportOutlined />}
              onClick={() => {
                handleReportBugDrawerOpen();
                handleToggle();
              }}
            />
            <Divider variant="fullWidth" component="li" />
            <HeaderListItem
              primaryText="Privacy Policy"
              icon={<PolicyOutlined />}
              onClick={() => {
                handlePrivacyPolicyDialogOpen();
                handleToggle();
              }}
            />
            <Divider variant="fullWidth" component="li" />
            <HeaderListItem
              primaryText="Terms of Service"
              icon={<DescriptionOutlined />}
              onClick={() => {
                handleTermsOfServiceDialogOpen();
                handleToggle();
              }}
            />
            <Divider variant="fullWidth" component="li" />
            <HeaderListItem
              primaryText="Tips"
              icon={<TipsAndUpdatesOutlined />}
              onClick={() => {
                handleTipsDialogOpen();
                handleToggle();
                // setIsStartingStep(true);
                // setActiveStep(0);
              }}
            />
            <Divider variant="fullWidth" component="li" />
            <Button
              variant="text"
              color="error"
              onClick={handleLogout}
              fullWidth
              sx={(theme) => ({
                paddingY: theme.spacing(1.5),
              })}
            >
              Log out
            </Button>
          </List>
        </CircularLoading>
      </HeaderPopup>
    </React.Fragment>
  );
};
